import { useCallback, useMemo, useState } from "react";
import { updateConsent } from "@/utils/ga4";

const CONSENT_KEY = "nexus-consent-preferences";
const DEFAULT_PREFERENCES = { analytics: true, marketing: false };

const readStoredConsent = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      return null;
    }
    const parsed = JSON.parse(stored);
    if (typeof parsed?.analytics !== "boolean" || typeof parsed?.marketing !== "boolean") {
      return null;
    }
    return { analytics: parsed.analytics, marketing: parsed.marketing };
  } catch (error) {
    return null;
  }
};

const persistConsent = (preferences) => {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(
    CONSENT_KEY,
    JSON.stringify({ ...preferences, updatedAt: new Date().toISOString() }),
  );
};

export const useConsentManager = () => {
  const getStoredConsent = useCallback(() => readStoredConsent(), [readStoredConsent]);
  const storedConsent = useMemo(() => getStoredConsent(), [getStoredConsent]);
  const [isVisible, setIsVisible] = useState(!storedConsent);
  const [preferences, setPreferences] = useState(storedConsent ?? DEFAULT_PREFERENCES);

  const applyConsent = useCallback(
    (nextPreferences) => {
      persistConsent(nextPreferences);
      updateConsent(nextPreferences);
      setPreferences(nextPreferences);
      setIsVisible(false);
    },
    [persistConsent, setIsVisible, setPreferences, updateConsent],
  );

  const handleToggle = useCallback(
    (key) => {
      setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
    },
    [setPreferences],
  );

  const handleSave = useCallback(() => {
    applyConsent(preferences);
  }, [applyConsent, preferences]);

  const handleAcceptAll = useCallback(() => {
    applyConsent({ analytics: true, marketing: true });
  }, [applyConsent]);

  const handleRejectAll = useCallback(() => {
    applyConsent({ analytics: false, marketing: false });
  }, [applyConsent]);

  return {
    isVisible,
    preferences,
    handleToggle,
    handleSave,
    handleAcceptAll,
    handleRejectAll,
  };
};
